import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../utils/apiClient';
import { useAuth } from '../auth/useAuth';

/**
 * Хук блокировки пользователя.
 * Загружает текущий статус и предоставляет block/unblock/toggle.
 *
 * @param {number} userId - ID пользователя, которого блокируем
 * @param {boolean} enabled - подгружать ли статус (например, не для себя)
 */
export function useBlock(userId, enabled = true) {
    const { isAuth } = useAuth();
    const [blocked, setBlocked] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Эндпоинт блокировок приватный — гостю его дёргать незачем:
        // блокировать он всё равно не может, а 401 только шумит в консоли.
        if (!enabled || !userId || !isAuth) return;

        let cancelled = false;
        (async () => {
            try {
                const res = await apiFetch(`/api/user/block/${userId}`, { method: 'GET' });
                const data = await res.json().catch(() => ({}));
                if (!cancelled && res.ok) setBlocked(!!data?.blocked);
            } catch {
                /* ignore */
            }
        })();

        return () => { cancelled = true; };
    }, [userId, enabled, isAuth]);

    const setStatus = useCallback(async (shouldBlock) => {
        if (!userId || loading) return;
        setLoading(true);
        try {
            const res = await apiFetch(`/api/user/block/${userId}`, {
                method: shouldBlock ? 'POST' : 'DELETE',
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) setBlocked(!!data?.blocked);
            return data;
        } finally {
            setLoading(false);
        }
    }, [userId, loading]);

    const block = useCallback(() => setStatus(true), [setStatus]);
    const unblock = useCallback(() => setStatus(false), [setStatus]);
    const toggle = useCallback(() => setStatus(!blocked), [setStatus, blocked]);

    return { blocked, loading, block, unblock, toggle };
}
